import { Badge } from '@/components/ui/badge';
import type { Parameter } from '@/types/openapi';

interface ParameterTableProps {
  parameters: Parameter[];
}

export function ParameterTable({ parameters }: ParameterTableProps) {
  if (!parameters || parameters.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">No parameters for this endpoint.</p>
    );
  }

  const getTypeDisplay = (schema?: Parameter['schema']): string => {
    if (!schema) return 'any';

    if (schema.type === 'array' && schema.items) {
      return `${getTypeDisplay(schema.items)}[]`;
    }

    if (schema.enum) {
      return schema.enum.join(' | ');
    }

    return schema.format || schema.type || 'any';
  };

  const getLocationBadgeVariant = (location: Parameter['in']): 'default' | 'secondary' | 'outline' => {
    switch (location) {
      case 'path':
        return 'default';
      case 'query':
        return 'secondary';
      case 'header':
        return 'outline';
      default:
        return 'secondary';
    }
  };

  const getLocationLabel = (location: Parameter['in']): string => {
    switch (location) {
      case 'path':
        return 'Path';
      case 'query':
        return 'Query';
      case 'header':
        return 'Header';
      case 'cookie':
        return 'Cookie';
      default:
        return location;
    }
  };

  return (
    <div className="rounded-lg border">
      <table className="w-full">
        <thead>
          <tr className="border-b bg-muted/50">
            <th className="px-4 py-3 text-left text-sm font-medium w-[25%]">Name</th>
            <th className="px-4 py-3 text-left text-sm font-medium w-[15%]">In</th>
            <th className="px-4 py-3 text-left text-sm font-medium w-[20%]">Type</th>
            <th className="px-4 py-3 text-left text-sm font-medium w-[10%]">Required</th>
            <th className="px-4 py-3 text-left text-sm font-medium w-[30%]">Description</th>
          </tr>
        </thead>
        <tbody>
          {parameters.map((param, index) => (
            <tr key={`${param.name}-${index}`} className="border-b last:border-b-0">
              <td className="px-4 py-3">
                <code className="text-sm font-mono text-blue-600 dark:text-blue-400">
                  {param.name}
                </code>
              </td>
              <td className="px-4 py-3">
                <Badge variant={getLocationBadgeVariant(param.in)}>
                  {getLocationLabel(param.in)}
                </Badge>
              </td>
              <td className="px-4 py-3">
                <span className="text-sm font-mono text-purple-600 dark:text-purple-400">
                  {getTypeDisplay(param.schema)}
                </span>
              </td>
              <td className="px-4 py-3">
                {param.required ? (
                  <Badge variant="destructive" className="text-xs">Required</Badge>
                ) : (
                  <span className="text-xs text-muted-foreground">Optional</span>
                )}
              </td>
              <td className="px-4 py-3 text-sm text-muted-foreground">
                {param.description || '-'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}